import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Badge,
} from 'reactstrap';
import { faTicket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../brand/BrandProvider';
import { useHasPermission } from '../../rbac/useHasPermission';
import { RelativeTime } from '../../components/RelativeTime';
import { useTicketsList } from './useTickets';
import { isOrbitApiProxyEnabled } from '../../orbit-api/orbitApiClient';

/**
 * Header-Integration für die Tickets-Inbox.
 *
 * - Badge zeigt die Anzahl der offenen+pending Tickets pro Brand.
 * - Dropdown listet die 5 jüngsten — Click öffnet den Drawer via
 *   /:brandSlug/tickets/:ticketId (vom TicketsPage abgefangen).
 *
 * Nur sichtbar wenn der Operator `tickets.read` hat. Polling alle 60 s
 * (useTicketsList hat eh `refetchInterval: 30s`, dieser Hook ist
 * derselbe Query-Key → React-Query dedupliziert).
 */
export function TicketsBell() {
  const canRead = useHasPermission({ perms: ['tickets.read'] });
  const brand = useBrand();
  const navigate = useNavigate();
  const brandSlug = brand.currentBrand?.slug;
  // Wenn ORBIT_API_URL serverseitig nicht gesetzt ist (BFF gibt 503 zurück),
  // den Bell gar nicht rendern statt alle paar Sekunden gegen den Proxy zu
  // pollen. Operator sieht dann einfach kein Tickets-Icon im Header.
  const orbitApiAvailable = isOrbitApiProxyEnabled();

  const query = useTicketsList({
    status: 'open,pending',
    limit: 5,
    enabled: orbitApiAvailable,
  });

  if (!canRead || !brandSlug || !orbitApiAvailable) return null;

  const items = query.data?.items ?? [];
  const count = items.length;

  return (
    <UncontrolledDropdown>
      <DropdownToggle color="link" className="p-0 position-relative" title="Offene Tickets">
        <FontAwesomeIcon
          icon={faTicket}
          className="text-secondary"
          style={{ fontSize: 22 }}
        />
        {count > 0 && (
          <Badge
            color="danger"
            pill
            className="position-absolute"
            style={{ top: -4, right: -8, fontSize: 10 }}
          >
            {count > 9 ? '9+' : count}
          </Badge>
        )}
      </DropdownToggle>
      <DropdownMenu end style={{ minWidth: 320 }}>
        <div className="px-3 py-2 d-flex justify-content-between align-items-center">
          <span className="fw-semibold small">Offene Tickets</span>
          <span className="text-muted small">{brand.currentBrand?.displayName}</span>
        </div>
        <DropdownItem divider />
        {query.isLoading && (
          <div className="px-3 py-2 text-muted small">Lade…</div>
        )}
        {!query.isLoading && items.length === 0 && (
          <div className="px-3 py-2 text-muted small">
            🎉 Keine offenen Tickets.
          </div>
        )}
        {items.map((t) => (
          <DropdownItem
            key={t.id}
            onClick={() =>
              navigate(`/${brandSlug}/tickets/${encodeURIComponent(t.id)}`)
            }
            className="px-3 py-2"
          >
            <div className="d-flex justify-content-between align-items-baseline gap-2">
              <code className="small">{t.ticketNumber}</code>
              <span className="small text-muted">
                {t.lastActivityAt && <RelativeTime iso={t.lastActivityAt} />}
              </span>
            </div>
            <div className="small text-truncate" style={{ maxWidth: 280 }}>
              {t.subject}
            </div>
            <div className="small text-muted text-truncate" style={{ maxWidth: 280 }}>
              {t.customer?.displayName ?? t.customer?.email ?? '—'}
            </div>
          </DropdownItem>
        ))}
        <DropdownItem divider />
        <DropdownItem
          onClick={() => navigate(`/${brandSlug}/tickets`)}
          className="px-3 small text-primary"
        >
          Alle Tickets öffnen →
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}
